 
"use client";

import {
  type ForwardedRef,
  type ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { CheckCircle, File, Flag, Mic, Paperclip, Send, ThumbsDown, ThumbsUp, Undo2, Users2, X } from "lucide-react";

import { MarkdownInlineContent, MarkdownWarnings } from "@/components/content";
import { IconButton } from "@/components/scirix/icon-button";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { formatTimestamp } from "@/lib/task-thread-normalizer";
import { useMarkdownDocument } from "@/hooks/useMarkdownDocument";
import type {
  ChatTypingIndicator,
  StateChangeEvent,
  SystemActionEvent,
  TaskPulseEvent,
  TaskPulseStage,
  TaskThreadAttachment,
  TaskThreadEvent,
  UserCommentEvent,
  InlineActionGroup,
} from "@/types/task-thread";

import { ChatMessageAvatar } from "./chat-message-avatar";
import { ChatMessageBubble, type ChatMessageBubbleTone } from "./chat-message-bubble";
import { ChatMessageIdentity } from "./chat-message-identity";
import { TaskPulseCard } from "./task-pulse-card";

type ThreadRenderBlock =
  | { kind: "state_change"; id: string; event: StateChangeEvent }
  | { kind: "task_pulse"; id: string; event: TaskPulseEvent }
  | { kind: "system_action"; id: string; event: SystemActionEvent }
  | { kind: "user_comment"; id: string; event: UserCommentEvent };

type ScrollToEventOptions = {
  behavior?: ScrollBehavior;
};

type ThreadFeedHandle = {
  scrollToEvent: (eventId: string, options?: ScrollToEventOptions) => boolean;
};

type ThreadFeedProps = {
  events: TaskThreadEvent[];
  isLoading: boolean;
  onLoadEarlier: () => void;
  onEmptyStateAction?: () => void;
  typingIndicators?: ChatTypingIndicator[];
  onPulseEntrySelect?: (taskPulseId: string, entryId: string) => void;
  onSystemAction?: (eventId: string, actionId: string) => void;
  hideEmptyStateMessage?: boolean;
};

export type ChatComposerAttachment = {
  id: string;
  name: string;
  previewUrl: string;
  sizeLabel: string;
  isImage: boolean;
};

export type ChatComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isSending: boolean;
  voiceEnabled: boolean;
  voiceActive?: boolean;
  voiceConnecting?: boolean;
  onToggleVoice?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  attachments: ChatComposerAttachment[];
  onSelectFiles: (files: FileList | File[] | null) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  attachmentError?: string | null;
  maxAttachmentCount?: number;
  maxAttachmentBytes?: number;
  placeholder?: string;
  sendLabel?: string;
  showAttachmentButton?: boolean;
  showVoiceButton?: boolean;
  showMentionButton?: boolean;
  extraActions?: ReactNode;
  footerHint?: ReactNode;
  onSendReview?: () => void;
  isSendingReview?: boolean;
  onSendReject?: () => void;
  isSendingReject?: boolean;
};

export type ChatThreadPanelProps = {
  events: TaskThreadEvent[];
  isLoading: boolean;
  onLoadEarlier: () => void;
  onEmptyStateAction?: () => void;
  composer: ChatComposerProps;
  composerRef?: ForwardedRef<HTMLTextAreaElement>;
  className?: string;
  typingIndicators?: ChatTypingIndicator[];
  onPulseEntrySelect?: (taskPulseId: string, entryId: string) => void;
  onSystemAction?: (eventId: string, actionId: string) => void;
  hideEmptyStateMessage?: boolean;
};

export type ChatThreadPanelHandle = {
  scrollToEvent: (eventId: string, options?: ScrollToEventOptions) => boolean;
};

export const ChatThreadPanel = forwardRef<ChatThreadPanelHandle, ChatThreadPanelProps>(
  function ChatThreadPanel({
    events,
    isLoading,
    onLoadEarlier,
    onEmptyStateAction,
    composer,
    composerRef,
    className,
    typingIndicators,
    onPulseEntrySelect,
    onSystemAction,
    hideEmptyStateMessage,
  }, ref) {
    const feedRef = useRef<ThreadFeedHandle>(null);

    useImperativeHandle(
      ref,
      () => ({
        scrollToEvent: (eventId: string, options?: ScrollToEventOptions) =>
          feedRef.current?.scrollToEvent(eventId, options) ?? false,
      }),
      [],
    );

    return (
      <div
        className={cn(
          "grid min-h-0 min-w-0 flex-1 grid-rows-[minmax(0,1fr)_auto] gap-4",
          className,
        )}
      >
        <ChatThreadFeed
          ref={feedRef}
          events={events}
          isLoading={isLoading}
          onLoadEarlier={onLoadEarlier}
          onEmptyStateAction={onEmptyStateAction}
          typingIndicators={typingIndicators}
          onPulseEntrySelect={onPulseEntrySelect}
          onSystemAction={onSystemAction}
          hideEmptyStateMessage={hideEmptyStateMessage}
        />
        <ChatComposer ref={composerRef} {...composer} />
      </div>
    );
  },
);

const ChatThreadFeed = forwardRef<ThreadFeedHandle, ThreadFeedProps>(function ChatThreadFeed(
  {
    events,
    isLoading,
    onLoadEarlier,
    onEmptyStateAction,
    typingIndicators = [],
    onPulseEntrySelect,
    onSystemAction,
    hideEmptyStateMessage = false,
  },
  ref,
) {
  const [pendingScrollRequest, setPendingScrollRequest] = useState<
    | {
        id: string;
        behavior?: ScrollBehavior;
      }
    | null
  >(null);
  const blockRefs = useRef(new Map<string, HTMLElement>());

  const blocks = useMemo<ThreadRenderBlock[]>(() => {
    return events.reduce<ThreadRenderBlock[]>((result, event) => {
      if (event.type === "system_action") {
        result.push({ kind: "system_action", id: event.id, event });
        return result;
      }

      if (event.type === "state_change") {
        result.push({ kind: "state_change", id: event.id, event });
        return result;
      }

      if (event.type === "task_pulse") {
        result.push({ kind: "task_pulse", id: event.id, event });
        return result;
      }

      result.push({ kind: "user_comment", id: event.id, event });
      return result;
    }, []);
  }, [events]);

  const blockEventIds = useMemo(() => {
    const ids = new Set<string>();
    blocks.forEach((block) => ids.add(block.event.id));
    return ids;
  }, [blocks]);

  useImperativeHandle(
    ref,
    () => ({
      scrollToEvent: (eventId: string, options?: ScrollToEventOptions) => {
        if (!blockEventIds.has(eventId)) {
          return false;
        }

        setPendingScrollRequest({ id: eventId, behavior: options?.behavior });
        return true;
      },
    }),
    [blockEventIds],
  );

  useEffect(() => {
    if (!pendingScrollRequest) {
      return undefined;
    }

    const targetRequest = pendingScrollRequest;
    let rafId: number | null = null;
    let attempts = 0;

    const tryScroll = () => {
      const target = blockRefs.current.get(targetRequest.id);
      if (target) {
        target.scrollIntoView({
          behavior: targetRequest.behavior ?? "smooth",
          block: "start",
        });
        setPendingScrollRequest((current) =>
          current?.id === targetRequest.id ? null : current,
        );
        return;
      }

      if (attempts < 4) {
        attempts += 1;
        rafId = window.requestAnimationFrame(tryScroll);
      } else {
        setPendingScrollRequest((current) =>
          current?.id === targetRequest.id ? null : current,
        );
      }
    };

    tryScroll();

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [pendingScrollRequest]);

  const hasEvents = blocks.length > 0;

  const createRefHandler = (block: ThreadRenderBlock) => (node: HTMLElement | null) => {
    const eventId = block.event.id;
    if (node) {
      blockRefs.current.set(eventId, node);
    } else {
      blockRefs.current.delete(eventId);
    }
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex justify-end text-xs">
        {hasEvents ? (
          <button
            type="button"
            onClick={onLoadEarlier}
            disabled={isLoading}
            className={cn(
              "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-muted-foreground transition",
              isLoading
                ? "cursor-wait border-border/40 bg-muted/40"
                : "border-border/60 hover:bg-muted/40 hover:text-foreground",
            )}
          >
            {isLoading ? "Loading…" : "Load earlier"}
          </button>
        ) : null}
      </div>
      {/*
       * Chat-style sticky-bottom: `mt-auto` pushes the ScrollArea to the
       * bottom of the feed wrapper (a flex column), so a sparse thread sits
       * just above the composer instead of leaving a gap between the latest
       * bubble and the composer. `max-h-full` caps the area at the wrapper's
       * height so longer threads still scroll internally rather than push
       * the composer off-screen.
       */}
      <ScrollArea className="mt-auto min-h-0 min-w-0 max-h-full pr-3" aria-label="Thread feed" role="region">
        <div className="min-w-0 space-y-3">
          {isLoading && (
            <div className="animate-pulse rounded-[var(--radius-xl)] border border-dashed border-border/60 px-4 py-3 text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
              Loading earlier activity…
            </div>
          )}
          {!isLoading && !hasEvents && !hideEmptyStateMessage ? (
            <div className="rounded-[var(--radius-xl)] border border-dashed border-border/60 bg-background/60 px-4 py-6 text-sm text-muted-foreground">
              <p className="font-semibold text-foreground">This task has not recorded any activity yet.</p>
              {onEmptyStateAction ? (
                <button
                  type="button"
                  onClick={onEmptyStateAction}
                  className="mt-3 inline-flex items-center gap-2 rounded-[var(--radius-full)] border border-border/60 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-foreground transition hover:border-foreground/60"
                >
                  Post the first comment
                </button>
              ) : null}
            </div>
          ) : null}
          {blocks.map((block, index) => {
            const refHandler = createRefHandler(block);
            const previousBlock = blocks[index - 1];

            if (block.kind === "state_change") {
              return (
                <StateChangePulseEntry
                  key={block.id}
                  event={block.event}
                  onSelectEntry={onPulseEntrySelect}
                  ref={refHandler}
                />
              );
            }

            if (block.kind === "task_pulse") {
              return (
                <TaskPulseEntry
                  key={block.id}
                  event={block.event}
                  onSelectEntry={onPulseEntrySelect}
                  ref={refHandler}
                />
              );
            }

            if (block.kind === "system_action") {
              return (
                <SystemActionCard
                  key={block.id}
                  event={block.event}
                  onSelectAction={(actionId) => onSystemAction?.(block.event.id, actionId)}
                  ref={refHandler}
                />
              );
            }

            const isStacked =
              previousBlock?.kind === "user_comment" &&
              previousBlock.event.author === block.event.author;

            return (
              <UserCommentCard
                key={block.id}
                event={block.event}
                isStacked={isStacked}
                ref={refHandler}
                onSelectInlineAction={(groupId, actionId) => onSystemAction?.(groupId, actionId)}
              />
            );
          })}
          {typingIndicators?.length ? (
            <div className="space-y-2">
              {typingIndicators.map((indicator) => (
                <TypingIndicatorCard key={indicator.id} indicator={indicator} />
              ))}
            </div>
          ) : null}
        </div>
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </div>
  );
});

const StateChangePulseEntry = forwardRef<
  HTMLDivElement,
  { event: StateChangeEvent; onSelectEntry?: (taskPulseId: string, entryId: string) => void }
>(function StateChangePulseEntry({ event, onSelectEntry }, ref) {
  const pulseEvent = mapStateChangeToPulseEvent(event);

  return (
    <div ref={ref}>
      <TaskPulseCard event={pulseEvent} onSelectEntry={onSelectEntry} />
    </div>
  );
});

const UserCommentCard = forwardRef<
  HTMLElement,
  { event: UserCommentEvent; isStacked?: boolean; onSelectInlineAction?: (groupId: string, actionId: string) => void }
>(
  function UserCommentCard({ event, isStacked, onSelectInlineAction }, ref) {
    const tone: ChatMessageBubbleTone =
      event.authorRole === "operator"
        ? "operator"
        : event.authorRole === "system"
          ? "system"
          : "external";
    const roleBadge = event.authorRole === "system" ? "Scirix" : event.author;
    const inlineAction = event.inlineAction;

    return (
      <div ref={ref as ForwardedRef<HTMLDivElement>} className="flex min-w-0 items-start gap-3">
        <ChatMessageIdentity
          name={event.author}
          role={event.authorRole}
          initials={event.avatarInitials}
          avatarImageUrl={event.avatarImageUrl}
          title={event.authorTitle}
          organization={event.authorOrg}
          handle={event.authorHandle}
          isStacked={isStacked}
        />
        <div className="flex-1 min-w-0">
          <ChatMessageBubble
            tone={tone}
            isStacked={isStacked}
            meta={`${roleBadge} · ${formatTimestamp(event.timestamp)}`}
            deliveryStatus={event.deliveryStatus}
            deliveryTimestamp={event.deliveryTimestamp}
            readReceipts={event.readReceipts}
          >
            <CommentMarkdown content={event.content} variant={tone === "operator" ? "inverse" : "default"} />
          </ChatMessageBubble>
          {event.attachments?.length ? <MessageAttachmentList attachments={event.attachments} /> : null}
          {inlineAction ? (
            <InlineActionRow
              action={inlineAction}
              onSelectAction={(actionId) => onSelectInlineAction?.(inlineAction.id, actionId)}
            />
          ) : null}
        </div>
      </div>
    );
  },
);

const TaskPulseEntry = forwardRef<HTMLDivElement, { event: TaskPulseEvent; onSelectEntry?: (taskPulseId: string, entryId: string) => void }>(
  function TaskPulseEntry({ event, onSelectEntry }, ref) {
    return (
      <div ref={ref}>
        <TaskPulseCard event={event} onSelectEntry={onSelectEntry} />
      </div>
    );
  },
);

const SystemActionCard = forwardRef<
  HTMLDivElement,
  { event: SystemActionEvent; onSelectAction?: (actionId: string) => void }
>(function SystemActionCard({ event, onSelectAction }, ref) {
  const actions = event.actions ?? [];
  const hasActions = actions.length > 0;
  const isCompleted = event.status === "completed" || !hasActions;

  const resolveVariant = (tone?: string) => {
    switch (tone) {
      case "primary":
        return "default" as const;
      case "danger":
        return "destructive" as const;
      default:
        return "secondary" as const;
    }
  };

  return (
    <div
      ref={ref}
      className="rounded-[var(--radius-xl)] border border-dashed border-border/60 bg-background/60 px-4 py-3 text-sm text-muted-foreground"
    >
      <p className="font-semibold text-foreground">{event.summary}</p>
      {event.resultSummary ? (
        <p className="mt-1 text-xs text-muted-foreground/80">{event.resultSummary}</p>
      ) : null}
      {hasActions ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {actions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant={resolveVariant(action.tone)}
              className="rounded-full px-4"
              disabled={isCompleted}
              onClick={() => {
                if (isCompleted) {
                  return;
                }
                onSelectAction?.(action.id);
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      ) : event.status === "completed" ? (
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.32em] text-muted-foreground/70">Completed</p>
      ) : null}
    </div>
  );
});

const ChatComposer = forwardRef<HTMLTextAreaElement, ChatComposerProps>(function ChatComposer(
  {
    value,
    onChange,
    onSend,
    isSending,
    voiceEnabled,
    voiceActive = false,
    voiceConnecting = false,
    onToggleVoice,
    disabled = false,
    disabledReason,
    attachments,
    onSelectFiles,
    onRemoveAttachment,
    attachmentError,
    maxAttachmentCount,
    maxAttachmentBytes,
    placeholder,
    sendLabel,
    showAttachmentButton = true,
    showVoiceButton = true,
    showMentionButton = true,
    extraActions,
    footerHint,
    onSendReview,
    isSendingReview = false,
    onSendReject,
    isSendingReject = false,
  },
  ref,
) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isComposerDisabled = disabled || isSending || isSendingReview || isSendingReject;
  const canSend = value.trim().length > 0 && !isComposerDisabled;
  const resolvedPlaceholder = placeholder ?? "Share an update, drop a reference link, or assign the next action.";
  const attachmentLimitHint = maxAttachmentCount
    ? `${attachments.length}/${maxAttachmentCount} attachments` +
      (maxAttachmentBytes ? ` · < ${Math.floor(maxAttachmentBytes / 1_048_576)} MB each` : "")
    : "Attach supporting images to your update.";
  const sendButtonLabel = isSending ? "Sending" : sendLabel ?? "Send Message";

  return (
    <div className="rounded-[var(--radius-xxl)] border border-border/60 bg-[var(--token-foundation-surface-subtle)] p-4 shadow-[var(--shadow-xs)]">
      {showAttachmentButton ? (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          multiple
          className="sr-only"
          tabIndex={-1}
          onChange={(event) => {
            onSelectFiles(event.target.files);
            event.target.value = "";
          }}
        />
      ) : null}
      <Textarea
        ref={ref}
        className="mt-3 min-h-[96px] resize-none rounded-[var(--radius-xl)] border border-border/60 bg-[var(--token-foundation-surface-base)] px-4 py-3 text-sm leading-relaxed text-foreground shadow-[var(--shadow-xs)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--token-foundation-surface-subtle)]"
        placeholder={resolvedPlaceholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={isComposerDisabled}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && canSend) {
            event.preventDefault();
            onSend();
          }
        }}
      />
      {showAttachmentButton && attachments.length ? (
        <ComposerAttachmentList
          className="mt-3"
          attachments={attachments}
          onRemove={onRemoveAttachment}
          disabled={isComposerDisabled}
        />
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2">
          {showAttachmentButton ? (
            <ComposerIconButton
              icon={Paperclip}
              label="Attach image"
              disabled={isComposerDisabled}
              onClick={() => {
                if (!isComposerDisabled) {
                  fileInputRef.current?.click();
                }
              }}
            />
          ) : null}
          {showVoiceButton ? (
            <ComposerIconButton
              icon={Mic}
              label={voiceActive ? "Voice session active" : "Start voice session"}
              disabled={isComposerDisabled || !voiceEnabled || voiceConnecting}
              ariaPressed={voiceActive}
              isBusy={voiceConnecting}
              onClick={voiceEnabled ? onToggleVoice : undefined}
            />
          ) : null}
          {showMentionButton ? (
            <ComposerIconButton icon={Users2} label="Mention operator" disabled={isComposerDisabled} />
          ) : null}
        </div>
        {extraActions}
        {onSendReject ? (
          <Button
            size="sm"
            variant="outline"
            className="gap-2 rounded-[var(--radius-full)] px-4 border-[color:var(--token-accent-warning-border)] text-[color:var(--token-accent-warning-strong)] hover:bg-[color:var(--token-accent-warning-surface)] hover:text-[color:var(--token-accent-warning-strong)]"
            onClick={onSendReject}
            disabled={isComposerDisabled || isSendingReject}
          >
            <Undo2 className="h-4 w-4" />
            {isSendingReject ? "Rejecting" : "Reject"}
          </Button>
        ) : null}
        {onSendReview ? (
          <Button
            size="sm"
            className="gap-2 rounded-[var(--radius-full)] px-4 bg-[color:var(--token-accent-success-core)] text-white hover:bg-[color:var(--token-accent-success-strong)] dark:hover:bg-[color:var(--token-accent-success-core)]"
            onClick={onSendReview}
            disabled={isComposerDisabled || isSendingReview}
          >
            <CheckCircle className="h-4 w-4" />
            {isSendingReview ? "Sending" : "Approve changes"}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant={onSendReview ? "outline" : "default"}
          className="gap-2 rounded-[var(--radius-full)] px-4"
          onClick={onSend}
          disabled={!canSend}
        >
          <Send className="h-4 w-4" />
          {sendButtonLabel}
        </Button>
      </div>
      {attachmentError ? <p className="mt-2 text-xs text-destructive">{attachmentError}</p> : null}
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground/80">
        {showAttachmentButton ? <span>{attachmentLimitHint}</span> : footerHint ? <span>{footerHint}</span> : null}
        <span>Ctrl + Enter to send</span>
      </div>
      {isComposerDisabled && disabledReason ? (
        <p className="mt-2 text-xs text-muted-foreground/80">{disabledReason}</p>
      ) : null}
    </div>
  );
});

type ComposerIconButtonProps = {
  icon: typeof Mic;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  ariaPressed?: boolean;
  isBusy?: boolean;
};

function ComposerIconButton({ icon: Icon, label, disabled, onClick, ariaPressed, isBusy }: ComposerIconButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-full)] border text-muted-foreground transition",
        disabled
          ? "cursor-not-allowed border-border/40 bg-muted/40"
          : "border-border/60 hover:bg-muted/40 hover:text-foreground",
      )}
      aria-label={label}
      aria-pressed={ariaPressed}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      <Icon className={cn("h-4 w-4", isBusy && "animate-pulse")} aria-hidden />
    </button>
  );
}

type ComposerAttachmentListProps = {
  attachments: ChatComposerAttachment[];
  onRemove: (attachmentId: string) => void;
  disabled?: boolean;
  className?: string;
};

function ComposerAttachmentList({ attachments, onRemove, disabled, className }: ComposerAttachmentListProps) {
  if (!attachments.length) {
    return null;
  }

  return (
    <ul className={cn("flex flex-wrap gap-3", className)}>
      {attachments.map((attachment) => (
        <li key={attachment.id} className="group relative w-[128px]">
          <div className="overflow-hidden rounded-[var(--radius-lg)] border border-border/70 bg-background/80 shadow-[var(--shadow-xs)]">
            {attachment.isImage ? (
              <div className="h-24 w-full bg-muted/40">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={attachment.previewUrl}
                  alt={attachment.name}
                  className="h-24 w-full object-cover"
                />
              </div>
            ) : (
              <div className="flex h-24 w-full flex-col items-center justify-center text-muted-foreground">
                <File className="h-6 w-6" aria-hidden />
              </div>
            )}
            <div className="px-2 py-2">
              <p className="truncate text-xs font-medium text-foreground">{attachment.name}</p>
              <p className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground/70">
                {attachment.sizeLabel}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onRemove(attachment.id)}
            className="absolute -right-2 -top-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-border/60 bg-background text-muted-foreground shadow-sm transition hover:text-foreground"
            aria-label={`Remove ${attachment.name}`}
            disabled={disabled}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

type MessageAttachmentListProps = {
  attachments: TaskThreadAttachment[];
};

function MessageAttachmentList({ attachments }: MessageAttachmentListProps) {
  if (!attachments.length) {
    return null;
  }

  return (
    <ul className="mt-3 flex flex-wrap gap-3">
      {attachments.map((attachment) => (
        <li key={attachment.id} className="w-[144px]">
          <a
            href={attachment.downloadUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="group block overflow-hidden rounded-[var(--radius-lg)] border border-border/70 bg-background/80 shadow-[var(--shadow-xs)]"
          >
            {attachment.isImage ? (
              <div className="h-24 w-full bg-muted/40">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={attachment.downloadUrl}
                  alt={attachment.name}
                  className="h-24 w-full object-cover transition duration-150 group-hover:scale-[1.02]"
                />
              </div>
            ) : (
              <div className="flex h-24 w-full flex-col items-center justify-center text-muted-foreground">
                <File className="h-6 w-6" aria-hidden />
              </div>
            )}
            <div className="px-2 py-2">
              <p className="truncate text-xs font-medium text-foreground">{attachment.name}</p>
              <p className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground/70">
                {formatAttachmentSize(attachment.size)}
              </p>
            </div>
          </a>
        </li>
      ))}
    </ul>
  );
}

type InlineActionRowProps = {
  action: InlineActionGroup;
  onSelectAction?: (actionId: string) => void;
};

function InlineActionRow({ action, onSelectAction }: InlineActionRowProps) {
  const hasActions = Boolean(action.actions?.length);
  const isCompleted = action.status === "completed" || !hasActions;

  if (isCompleted) {
    if (!action.resultSummary) {
      return null;
    }
    return (
      <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/70">
        {action.resultSummary}
      </p>
    );
  }

  return (
    <div className="mt-2 flex items-center gap-2 text-muted-foreground/80">
      <span className="text-[11px] font-semibold uppercase tracking-[0.24em]">
        {action.summary ?? "Feedback"}
      </span>
      <div className="flex gap-1">
        {action.actions?.map((option) => {
          const Icon = resolveInlineActionIcon(option.id);
          return (
            <IconButton
              key={option.id}
              ariaLabel={option.label}
              icon={Icon}
              className={cn(
                option.tone === "primary" && "text-foreground",
                option.tone === "danger" && "text-destructive",
              )}
              onClick={() => onSelectAction?.(option.id)}
            />
          );
        })}
      </div>
    </div>
  );
}

function resolveInlineActionIcon(actionId: string) {
  if (actionId.includes("thumbs_down")) {
    return ThumbsDown;
  }
  if (actionId.includes("flag")) {
    return Flag;
  }
  return ThumbsUp;
}

function mapStateChangeToPulseEvent(event: StateChangeEvent): TaskPulseEvent {
  const author = event.author?.trim();
  const summary = author ? `${event.label} by ${author}` : event.label;
  const taskLabel = author ? `${event.label} by ${author}` : `State change: ${event.label}`;

  return {
    type: "task_pulse",
    id: `state-pulse-${event.id}`,
    taskId: `state-${event.id}`,
    taskLabel,
    entries: [
      {
        id: event.id,
        timestamp: event.timestamp,
        stage: stageForStateChangeLabel(event.label),
        summary,
        detail: event.detail,
      },
    ],
  };
}

function stageForStateChangeLabel(label: string): TaskPulseStage {
  const normalized = label.trim().toLowerCase();
  if (/(^|\s)(fail|error)/.test(normalized)) return "error";
  if (normalized.includes("merged") || normalized.includes("finished") || normalized.includes("closed")) return "finished";
  if (normalized.includes("merging") || normalized.includes("rebasing") || normalized.includes("pushing")) return "executing";
  // Status changes, "Started working", "Paused", etc. are progression markers —
  // keep them neutral instead of tagging them with a completion badge.
  return "orchestrating";
}

type TypingIndicatorCardProps = {
  indicator: ChatTypingIndicator;
};

function TypingIndicatorCard({ indicator }: TypingIndicatorCardProps) {
  if (!indicator.participants.length && !indicator.message) {
    return null;
  }

  const label = indicator.message ?? formatTypingIndicatorLabel(indicator);

  return (
    <div className="flex items-center gap-3 rounded-[var(--radius-xl)] border border-dashed border-border/60 bg-background/70 px-4 py-3" aria-live="polite">
      <div className="flex -space-x-2">
        {indicator.participants.slice(0, 3).map((participant) => (
          <ChatMessageAvatar
            key={participant.id}
            initials={participant.avatarInitials}
            variant={participant.role}
            size="sm"
            className="h-9 w-9 border-2 border-background text-[11px]"
            alt={participant.name}
          />
        ))}
      </div>
      <div className="min-w-0 text-sm">
        <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-muted-foreground/80">Typing</p>
        <p className="text-sm font-medium text-foreground">{label}</p>
      </div>
      <div className="ml-auto inline-flex items-center gap-1" aria-hidden>
        {[0, 1, 2].map((index) => (
          <span
            key={index}
            className="inline-flex h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/80"
            style={{ animationDelay: `${index * 120}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function formatTypingIndicatorLabel(indicator: ChatTypingIndicator) {
  const { participants } = indicator;
  if (participants.length === 0) {
    return "A participant is typing…";
  }

  if (participants.length === 1) {
    return `${participants[0].name} is typing…`;
  }

  if (participants.length === 2) {
    return `${participants[0].name} and ${participants[1].name} are typing…`;
  }

  return `${participants[0].name} and ${participants.length - 1} others are typing…`;
}

function formatAttachmentSize(size: number) {
  if (!size || size < 1024) {
    return `${size ?? 0} B`;
  }

  if (size < 1_048_576) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / 1_048_576).toFixed(1)} MB`;
}

type CommentMarkdownProps = {
  content: string;
  variant?: "default" | "inverse";
};

function CommentMarkdown({ content, variant = "default" }: CommentMarkdownProps) {
  // Escape bare HTML/XML tags outside of markdown code spans/blocks so they
  // display as literal text instead of being stripped by rehypeSanitize.
  // We only escape angle brackets that look like tags (< followed by a letter
  // or /) but leave markdown-standard constructs (code blocks, backticks) alone.
  const escaped = content.replace(
    /(`{1,3}[\s\S]*?`{1,3})|(<\/?[a-zA-Z][^>]*\/?>)/g,
    (match, codeSpan) => (codeSpan ? match : match.replace(/</g, "&lt;").replace(/>/g, "&gt;")),
  );
  const { document, warnings } = useMarkdownDocument(escaped);
  const hasContent = document.blocks.length > 0;

  if (!hasContent && warnings.length === 0) {
    return null;
  }

  const textToneClass =
    variant === "inverse" ? "text-[var(--token-surface-default)]" : "text-foreground";

  return (
    <div className="space-y-3">
      {hasContent ? (
        <MarkdownInlineContent
          document={document}
          className={cn("text-sm leading-relaxed", textToneClass)}
        />
      ) : null}
      {warnings.length ? (
        <MarkdownWarnings
          warnings={warnings}
          className={cn(
            "text-left",
            variant === "inverse" && "text-[var(--token-surface-default)]/80",
          )}
        />
      ) : null}
    </div>
  );
}
